import { BSC_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback, useState } from 'react';
import { useAccount } from 'wagmi';
import { readContract } from 'wagmi/actions';

import { apiClientV1 } from '@/agent/apiClient/api';
import { NODE_SALE_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import type { GetPriceFeedProps, GetPriceInUSDProps, PriceFeed, PriceV1Hook } from '@/hooks/types/price.type';
import { useFetchPricesV1Mutation } from '@/store/services/price.service';
import { useAppSelector } from '@/store/utils';

import { useWeb3 } from '../useWeb3';

const INITIAL_PRICE_FEED: PriceFeed = {
  price: 0,
  prices: { bsc: 0, lix: 0, usdt: 0, usdc: 0 },
  original: null,
};

const usePriceV1 = (): PriceV1Hook => {
  const { address } = useAccount();
  const { config } = useWeb3();
  const { nodeId, referralCode, address: receivedAddress } = useAppSelector(state => state.app);
  const [priceFeed, setPriceFeed] = useState<PriceFeed>(INITIAL_PRICE_FEED);
  const [priceInUsd, setPriceInUsd] = useState<number>(0);
  const [needToSign, setNeedToSign] = useState<boolean>(false);
  const [fetchPricesV1] = useFetchPricesV1Mutation();

  const {
    value: isFetchingPriceInUSD,
    setTrue: setIsFetchingPriceInUSDTrue,
    setFalse: setIsFetchingPriceInUSDFalse,
  } = useBoolean(false);

  const {
    value: isFetchingPriceFeed,
    setTrue: setIsFetchingPriceFeedTrue,
    setFalse: setIsFetchingPriceFeedFalse,
  } = useBoolean(false);

  const getPriceInUSD = useCallback(
    async ({
      amount,
      currentTierPrice = 0,
      isLoadingAgain = true,
    }: GetPriceInUSDProps): Promise<number | undefined> => {
      if (!nodeId) return;
      isLoadingAgain && setIsFetchingPriceInUSDTrue();
      let price = (currentTierPrice * amount) / 10 ** USD_VALUE_DECIMAL;

      try {
        if (address) {
          const data = await readContract(config, {
            abi: BSC_ABI,
            address: NODE_SALE_V1_SC_ADDRESS,
            functionName: 'getUSDValue',
            args: [nodeId, amount],
          });
          price = Number(data) / 10 ** USD_VALUE_DECIMAL;
        }
        isLoadingAgain && setPriceInUsd(price);
        return price;
      } catch (error) {
        isLoadingAgain && setPriceInUsd(price);
        return price;
      } finally {
        setIsFetchingPriceInUSDFalse();
      }
    },
    [nodeId, address, config],
  );

  const getPriceFeed = useCallback(
    async ({ coin, nft, amount = 1 }: GetPriceFeedProps & { nft?: string }) => {
      setIsFetchingPriceFeedTrue();
      let price = INITIAL_PRICE_FEED;

      try {
        const priceData = await fetchPricesV1({
          amount,
          buyerAddress: receivedAddress !== '' ? receivedAddress : address,
          coin,
          nft: nft ?? nodeId,
          ...(referralCode !== '' && { referralCode }),
        });
        if (priceData.data) {
          price = {
            price: ((priceData.data.price ?? 0) * amount) / 10 ** priceData.data.decimals,
            prices: priceData.data.prices,
            original: priceData.data,
          };
        }

        setPriceFeed(price);
        return price;
      } catch (error: any) {
        setPriceFeed(INITIAL_PRICE_FEED);
        throw error;
      } finally {
        setIsFetchingPriceFeedFalse();
      }
    },
    [address, receivedAddress, nodeId, referralCode, fetchPricesV1],
  );

  const checkRegisterOrNot = useCallback(
    async ({ coin, amount = 1 }: GetPriceFeedProps) => {
      try {
        await apiClientV1.post(`v1/price/get`, { coin, buyerAddress: address, nft: nodeId, amount });
        setNeedToSign(false);
      } catch (error: any) {
        if (error.response?.data?.code === '5000') {
          setNeedToSign(true);
        } else {
          throw error;
        }
      }
    },
    [address, nodeId],
  );

  return {
    needToSign,
    priceFeed,
    priceInUsd,
    isFetchingPriceFeed,
    isFetchingPriceInUSD,
    getPriceFeed,
    getPriceInUSD,
    setNeedToSign,
    checkRegisterOrNot,
  };
};

export { usePriceV1 };
